import type { Database as DB, TelemetrySample } from '../store/Database';
import type { StateManager } from '../core/StateManager';
import type { TripManager } from '../core/TripManager';
import type { PredictiveHealth } from '../core/PredictiveHealth';
import type { AuditLogger } from '../services/security/AuditLogger';
import type { TrendDashboard } from '../services/analytics/TrendDashboard';
import { Terminal } from './Terminal';
import { calculateWriteValue } from '../core/PhysicsEngine';
import { ModbusRTU } from '../modbus/ModbusRTU';
import { DRIVE_PROFILES } from '../config/profiles';
import { ExportService } from '../services';
import type { TripKey } from '../types/drive';
import { pushTelemetry } from '../services/api/telemetry';

export interface PanelDom {
  simTargetFreq: HTMLInputElement;
  simTargetFreqVal: HTMLElement;
  simLoad: HTMLInputElement;
  simLoadVal: HTMLElement;
  simVoltageDrop: HTMLInputElement;
  simVoltageDropVal: HTMLElement;
  simImbalance: HTMLInputElement;
  simImbalanceVal: HTMLElement;
  simDaysOffline: HTMLInputElement;
  simDaysOfflineVal: HTMLElement;
  simLooseTerminal: HTMLInputElement;
  simCloggedHeatsink: HTMLInputElement;
  tripBanner: HTMLElement;
  tripBannerText: HTMLElement;
  tripBannerReset: HTMLElement;
  fanMainBar: HTMLElement;
  fanMainHoursText: HTMLElement;
  fanControlBar: HTMLElement;
  fanControlHoursText: HTMLElement;
  capReformingStatus: HTMLElement;
  clearTerminalBtn: HTMLButtonElement;
}

export interface HealthBindings {
  updateFanHealth(): void;
  updateCapacitorHealth(): void;
}

export function setupTripControls(
  dom: Pick<PanelDom, 'tripBanner' | 'tripBannerText' | 'tripBannerReset'>,
  tripManager: TripManager,
  terminal: Terminal,
  audit: AuditLogger,
): void {
  const tripKeys: TripKey[] = ['OC', 'OV', 'UV', 'OT', 'FAN'];
  tripKeys.forEach((key) => {
    const btn = document.getElementById(`trip${key.charAt(0) + key.slice(1).toLowerCase()}`);
    btn?.addEventListener('click', () => {
      const desc = tripManager.triggerTrip(key);
      if (desc) {
        dom.tripBannerText.innerText = `INVERSOR DESARMADO: ${desc}`;
        dom.tripBanner.style.display = 'flex';
        terminal.log(`TRIP DETECTADO: ${desc}`, 'error');
        audit.log('trip.trigger', `Trip disparado: ${desc}`, 'error');
      }
    });
  });

  const resetTripBtn = document.getElementById('tripNone');
  resetTripBtn?.addEventListener('click', () => {
    tripManager.resetTrip();
    dom.tripBanner.style.display = 'none';
    terminal.log('RESET: Sistema restaurado e pronto para operação', 'system');
    audit.log('trip.reset', 'Reset de trip via botão TripNone', 'info');
  });

  dom.tripBannerReset.addEventListener('click', () => {
    tripManager.resetTrip();
    dom.tripBanner.style.display = 'none';
    terminal.log('RESET: Sistema restaurado e pronto para operação', 'system');
    audit.log('trip.reset', 'Reset de trip via banner', 'info');
  });
}

export function setupSimulationControls(
  dom: Pick<
    PanelDom,
    | 'simTargetFreq' | 'simTargetFreqVal'
    | 'simLoad' | 'simLoadVal'
    | 'simVoltageDrop' | 'simVoltageDropVal'
    | 'simImbalance' | 'simImbalanceVal'
    | 'simDaysOffline' | 'simDaysOfflineVal'
    | 'simLooseTerminal'
    | 'simCloggedHeatsink'
  >,
  state: StateManager,
  terminal: Terminal,
  updateCapacitorHealth: () => void,
): void {
  dom.simTargetFreq.addEventListener('input', () => {
    const freq = parseFloat(dom.simTargetFreq.value);
    state.set('targetFreq', freq);
    dom.simTargetFreqVal.innerText = `${freq.toFixed(1)} Hz`;

    const profileKey = state.get('selectedProfileKey');
    const profile = DRIVE_PROFILES[profileKey];
    if (!profile) return;

    const rawVal = calculateWriteValue(freq, profile);
    const reqFrame = ModbusRTU.buildWriteRequest(
      state.get('slaveId'),
      profile.regWriteFreq,
      rawVal,
    );
    terminal.log(
      `FC06 Write Setpoint (${profile.regWriteFreq}): ${ModbusRTU.bytesToHex(reqFrame)}`,
      'tx',
    );
  });

  dom.simLoad.addEventListener('input', () => {
    const val = parseInt(dom.simLoad.value);
    state.set('load', val);
    dom.simLoadVal.innerText = `${val}%`;
  });

  dom.simVoltageDrop.addEventListener('input', () => {
    const val = parseInt(dom.simVoltageDrop.value);
    state.set('voltageDrop', val);
    dom.simVoltageDropVal.innerText = `-${val} V`;
  });

  dom.simImbalance.addEventListener('input', () => {
    const val = parseInt(dom.simImbalance.value);
    state.set('imbalance', val);
    dom.simImbalanceVal.innerText = `${val}%`;
  });

  dom.simDaysOffline.addEventListener('input', () => {
    const val = parseInt(dom.simDaysOffline.value);
    state.set('daysOffline', val);
    dom.simDaysOfflineVal.innerText = `${val} dias`;
    updateCapacitorHealth();
  });

  dom.simLooseTerminal.addEventListener('change', () => {
    state.set('looseTerminal', dom.simLooseTerminal.checked);
  });

  dom.simCloggedHeatsink.addEventListener('change', () => {
    state.set('cloggedHeatsink', dom.simCloggedHeatsink.checked);
  });
}

export function setupExportButtons(
  db: DB,
  state: StateManager,
  terminal: Terminal,
  audit: AuditLogger,
): void {
  document.getElementById('btnExportCSV')?.addEventListener('click', async () => {
    try {
      const samples = await db.telemetry.orderBy('timestamp').reverse().limit(100).toArray();
      ExportService.downloadCSV(samples.reverse());
      terminal.log('CSV exportado com sucesso', 'system');
      audit.log('export.csv', `CSV exportado (${samples.length} amostras)`, 'info');
    } catch {
      terminal.log('Falha ao exportar CSV', 'error');
      audit.log('export.csv', 'Falha ao exportar CSV', 'error');
    }
  });

  document.getElementById('btnExportPDF')?.addEventListener('click', async () => {
    try {
      const samples = await db.telemetry.orderBy('timestamp').reverse().limit(50).toArray();
      const alarms = await db.alarms.orderBy('timestamp').reverse().limit(20).toArray();
      await ExportService.downloadReport(state.getSnapshot(), samples.reverse(), alarms.reverse());
      terminal.log('PDF exportado com sucesso', 'system');
      audit.log('export.pdf', 'PDF exportado', 'info');
    } catch {
      terminal.log('Falha ao exportar PDF', 'error');
      audit.log('export.pdf', 'Falha ao exportar PDF', 'error');
    }
  });
}

export function setupKeyboardShortcuts(
  dom: Pick<PanelDom, 'tripBanner' | 'clearTerminalBtn'>,
  tripManager: TripManager,
  terminal: Terminal,
): void {
  document.addEventListener('keydown', (e) => {
    if (e.key === 'r' || e.key === 'R') {
      if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement)) {
        tripManager.resetTrip();
        dom.tripBanner.style.display = 'none';
        terminal.log('RESET [R]: Sistema restaurado', 'system');
      }
    }
    if (e.key === 'c' || e.key === 'C') {
      if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement)) {
        dom.clearTerminalBtn.click();
      }
    }
  });
}

export function setupHealthBindings(
  dom: Pick<PanelDom, 'fanMainBar' | 'fanMainHoursText' | 'fanControlBar' | 'fanControlHoursText' | 'capReformingStatus'>,
  state: StateManager,
  predictiveHealth: PredictiveHealth,
): HealthBindings {
  function updateFanHealth(): void {
    const health = predictiveHealth.getFanHealth();
    dom.fanMainBar.style.width = `${health.mainPercent}%`;
    dom.fanMainBar.style.backgroundColor = health.mainColor;
    dom.fanMainHoursText.innerText = `${state.get('fanMainHours').toLocaleString()}h / vida util`;
    dom.fanControlBar.style.width = `${health.controlPercent}%`;
    dom.fanControlBar.style.backgroundColor = health.controlColor;
    dom.fanControlHoursText.innerText = `${state.get('fanControlHours').toLocaleString()}h / vida util`;
  }

  function updateCapacitorHealth(): void {
    const health = predictiveHealth.getCapacitorHealth();
    dom.capReformingStatus.innerText = health.status;
    dom.capReformingStatus.style.color = health.color;
  }

  state.subscribe('fanMainHours', updateFanHealth);
  state.subscribe('fanControlHours', updateFanHealth);

  return { updateFanHealth, updateCapacitorHealth };
}

export function setupTelemetrySampling(
  db: DB,
  state: StateManager,
  trendDashboard: TrendDashboard,
): void {
  setInterval(async () => {
    try {
      const sample: TelemetrySample = {
        timestamp: new Date(),
        profileKey: state.get('selectedProfileKey'),
        frequency: state.get('currentFreq'),
        current: state.get('currentAmp'),
        dcVoltage: state.get('dcVoltage'),
        temperature: state.get('heatsinkTemp'),
        load: state.get('load'),
      };
      await db.recordSample(sample);
      trendDashboard.addSample(sample);
      pushTelemetry(sample);
    } catch {
      // silent
    }
  }, 10000);
}